import { useEffect, useMemo, useRef, useState } from 'react';

import { getOfferBids } from 'PrivateApis';

import { getTimeLeft, isNotValidOffer } from '../acceptOffer/AcceptOffer.helpers';
import { OfferPropTypes } from '../acceptOffer/AcceptOffer.types';

export type useGetOfferBidsProps = Partial<{
  dpEnquiryId: number;
  onError: () => void;
  vrm: string;
}>;

type UseGetOfferBids = {
  loading: boolean;
  offer: OfferPropTypes | null;
  timeLeft: number | undefined;
}

export const useGetOfferBids = ({ dpEnquiryId, onError, vrm }: useGetOfferBidsProps): UseGetOfferBids => {
  const [offer, setOffer] = useState<UseGetOfferBids['offer']>(null);
  const [timeLeft, setTimeLeft] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const abortController = useMemo(() => new AbortController(), []);
  const cancelled = useRef<boolean>(false);

  useEffect(() => () => {
    cancelled.current = true;
  }, [abortController]);

  useEffect(() => {
    if (!dpEnquiryId) {
      onError?.();
      return;
    }

    const fetchOfferBids = async () => {
      try {
        setLoading(true);

        const data = await getOfferBids(dpEnquiryId, abortController);

        // This is temporary until we call this solely on the confirm sale page
        // as the checks for status would be inaccurate
        if (isNotValidOffer(data, vrm)) {
          onError?.();
          return;
        }

        const { offerExpiry } = data;

        if (cancelled.current) {
          return;
        }

        setOffer(data);
        setTimeLeft(getTimeLeft(offerExpiry));

        // Expose vehicle data to the global scope for Optimizely Web AB testing.
        // eslint-disable-next-line no-underscore-dangle
        (globalThis as any).__OPTIMIZELY_WEB_DATA__ = (globalThis as any).__OPTIMIZELY_WEB_DATA__ || {};
        // eslint-disable-next-line no-underscore-dangle
        (globalThis as any).__OPTIMIZELY_WEB_DATA__.enquiryOfferBids = Object.freeze(data);
      } catch (err) {
        if (cancelled.current) {
          return;
        }
        window?.Sentry?.captureException?.(err);
        onError?.();
      } finally {
        if (!cancelled.current) {
          setLoading(false);
        }
      }
    };

    if (!offer && dpEnquiryId) {
      fetchOfferBids();
    }
  }, [abortController, dpEnquiryId, offer, onError, vrm]);

  return {
    loading,
    offer,
    timeLeft,
  };
};
